import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, FeatureGroup, Polygon, Popup, CircleMarker, Marker, useMap } from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import apiClient from '../../utils/apiclient';

// Fix Leaflet's default icon issue with bundlers (same pattern as AllRiders.jsx)
let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconAnchor: [12, 41]
});
L.Marker.prototype.options.icon = DefaultIcon;

// Gurugram coordinates as default center
const DEFAULT_CENTER = [28.4595, 77.0266];
const ZOOM_LEVEL = 12;

const MapUpdater = ({ selectedZone, restaurantLocation, zones, initialFitDone, onFitDone, focusRestaurant }) => {
  const map = useMap();
  useEffect(() => {
    if (focusRestaurant && restaurantLocation) {
      map.flyTo([restaurantLocation.latitude, restaurantLocation.longitude], Math.max(map.getZoom(), 15), { duration: 0.8 });
    } else if (selectedZone && selectedZone.coordinates && selectedZone.coordinates.length > 0) {
      const bounds = L.latLngBounds(selectedZone.coordinates.map(c => [c.latitude, c.longitude]));
      const isMobile = window.innerWidth < 768;
      map.fitBounds(bounds, { padding: isMobile ? [40, 40] : [50, 50], maxZoom: 16 });
    } else if (!initialFitDone && restaurantLocation) {
      // Initial load: fit restaurant + all zones so both are visible
      const points = [];
      points.push([restaurantLocation.latitude, restaurantLocation.longitude]);
      zones.forEach(z => {
        (z.coordinates || []).forEach(c => {
          if (c.latitude != null && c.longitude != null) points.push([c.latitude, c.longitude]);
        });
      });
      if (points.length === 1) {
        map.setView(points[0], Math.max(map.getZoom(), 14));
      } else if (points.length > 1) {
        const bounds = L.latLngBounds(points);
        const isMobile = window.innerWidth < 768;
        map.fitBounds(bounds, { padding: isMobile ? [60, 60] : [70, 70], maxZoom: 16 });
      }
      if (onFitDone) onFitDone();
    }
  }, [selectedZone, restaurantLocation, zones, initialFitDone, focusRestaurant, map]);
  return null;
};

// Clears the restaurant focus when the user manually pans/zooms the map.
// Programmatic moves (flyTo/fitBounds right after clicking the card) are ignored
// via the focus timestamp ref.
const MapDeselectOnMove = ({ lastFocusRef, onUserMove }) => {
  const map = useMap();
  useEffect(() => {
    const handleUserMove = () => {
      if (Date.now() - lastFocusRef.current < 2000) return;
      onUserMove();
    };
    map.on('dragstart', handleUserMove);
    map.on('zoomstart', handleUserMove);
    return () => {
      map.off('dragstart', handleUserMove);
      map.off('zoomstart', handleUserMove);
    };
  }, [map, lastFocusRef, onUserMove]);
  return null;
};
const extractRestaurantLatLng = (obj) => {
  if (!obj || typeof obj !== 'object') return null;
  const candidates = [
    // flat variations
    [obj.latitude, obj.longitude],
    [obj.lat, obj.lng],
    [obj.lat, obj.long],
    [obj.lat, obj.longitude],
    [obj.latitude, obj.lng],
    [obj.restaurantLatitude, obj.restaurantLongitude],
    // nested variations
    [obj.location?.latitude, obj.location?.longitude],
    [obj.location?.lat, obj.location?.lng],
    [obj.address?.latitude, obj.address?.longitude],
    [obj.address?.lat, obj.address?.lng],
    [obj.coordinates?.latitude, obj.coordinates?.longitude],
    [obj.coords?.latitude, obj.coords?.longitude],
  ];
  for (const [lat, lng] of candidates) {
    const parsedLat = parseFloat(lat);
    const parsedLng = parseFloat(lng);
    if (Number.isFinite(parsedLat) && Number.isFinite(parsedLng)) {
      return { latitude: parsedLat, longitude: parsedLng };
    }
  }
  return null;
};

const RestaurantDeliveryZones = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [zones, setZones] = useState([]);
  const [restaurantName, setRestaurantName] = useState("");
  const [restaurantLocation, setRestaurantLocation] = useState(null);
  const [initialFitDone, setInitialFitDone] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);

  // States for naming modal
  const [showNameModal, setShowNameModal] = useState(false);
  const [pendingZoneLayer, setPendingZoneLayer] = useState(null);
  const [newZoneName, setNewZoneName] = useState("");
  const [zoneToRemove, setZoneToRemove] = useState(null);
  const [selectedZoneIndex, setSelectedZoneIndex] = useState(null);
  const [isRestaurantSelected, setIsRestaurantSelected] = useState(false);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const lastRestaurantFocusTime = useRef(0);

  // Stamp every restaurant focus so programmatic map moves aren't mistaken for manual ones
  useEffect(() => {
    if (isRestaurantSelected) lastRestaurantFocusTime.current = Date.now();
  }, [isRestaurantSelected]);

  const handleUserMapMove = useCallback(() => setIsRestaurantSelected(false), []);

  const featureGroupRef = useRef(null);

  useEffect(() => {
    fetchZones();
  }, [id]);

  const fetchZones = async () => {
    try {
      setLoading(true);

      // Fetch zones, restaurant info, and restaurant list (lat/lng lives in /restaurant list)
      const [zonesRes, infoRes, listRes] = await Promise.all([
        apiClient.get(`/restaurants/${id}/delivery-zones`).catch(err => {
          console.error("Error fetching zones:", err);
          return { data: { zones: [] } };
        }),
        apiClient.get(`/restaurantInfo/${id}`).catch(err => {
          console.error("Error fetching restaurant info:", err);
          return { data: { name: `Restaurant ${id}` } };
        }),
        apiClient.get(`/restaurant`).catch(err => {
          console.error("Error fetching restaurant list:", err);
          return { data: [] };
        })
      ]);

      if (infoRes.data && infoRes.data.name) {
        setRestaurantName(infoRes.data.name);
      } else {
        setRestaurantName(`Restaurant ${id}`);
      }

      // Restaurant marker location comes from restaurantInfo response (lat/lng variations handled)
      const infoPayload = infoRes.data?.data || infoRes.data;
      let restLoc = extractRestaurantLatLng(infoPayload);
      // Fallback: some backends include restaurant lat/lng inside delivery-zones response
      if (!restLoc) {
        restLoc = extractRestaurantLatLng(zonesRes.data?.data || zonesRes.data);
      }
      // Fallback: /restaurant list response carries latitude/longitude per restaurant
      if (!restLoc && Array.isArray(listRes.data)) {
        const match = listRes.data.find(r => Number(r.id) === Number(id));
        if (match) {
          const lat = parseFloat(match.latitude ?? match.lat);
          const lng = parseFloat(match.longitude ?? match.lng ?? match.long);
          // Reject 0 / out-of-range values (bad data, e.g. latitude 0.0)
          if (Number.isFinite(lat) && Number.isFinite(lng) && lat !== 0 && lng !== 0
            && Math.abs(lat) <= 90 && Math.abs(lng) <= 180) {
            restLoc = { latitude: lat, longitude: lng };
          }
        }
      }
      if (restLoc) {
        setRestaurantLocation(restLoc);
        setMapCenter([restLoc.latitude, restLoc.longitude]);
      } else {
        setRestaurantLocation(null);
      }
      setInitialFitDone(false);
      setIsRestaurantSelected(false);

      const response = zonesRes;
      if (response.data && response.data.zones) {
        const normalizedZones = response.data.zones.map(z => ({
          zoneName: z.zoneName || z.name || '',
          active: z.active,
          coordinates: z.coordinates.map(c => ({
            latitude: c.latitude,
            longitude: c.longitude
          }))
        }));
        setZones(normalizedZones);

        // Prefer restaurant location for centering; fall back to first zone coord
        if (!restLoc && normalizedZones.length > 0 && normalizedZones[0].coordinates.length > 0) {
          const firstCoord = normalizedZones[0].coordinates[0];
          setMapCenter([firstCoord.latitude, firstCoord.longitude]);
        }
      } else {
        setZones([]);
      }
    } catch (err) {
      console.error("Error fetching zones:", err);
      setZones([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveZones = async () => {
    try {
      setSaving(true);
      const payload = { zones: zones.map(({ _tempId, ...rest }) => rest) }; // remove temp UI ids
      console.log("payload : ", payload)
      await apiClient.post(`/restaurants/${id}/delivery-zones`, payload);
      alert("Delivery zones saved successfully!");
    } catch (err) {
      console.error("Error saving zones:", err);
      alert("Failed to save delivery zones. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const onCreated = (e) => {
    const { layerType, layer } = e;
    if (layerType === 'polygon') {
      setPendingZoneLayer(layer);
      setNewZoneName("");
      setShowNameModal(true);
    }
  };

  const handleNameSubmit = () => {
    if (!newZoneName || newZoneName.trim() === '') {
      alert("Zone name is required.");
      return;
    }

    const latlngs = pendingZoneLayer.getLatLngs()[0];
    const coordinates = latlngs.map(ll => ({
      latitude: ll.lat,
      longitude: ll.lng
    }));

    const newZone = {
      zoneName: newZoneName.trim(),
      active: true,
      coordinates: coordinates,
      _tempId: Date.now()
    };

    setZones(prev => [...prev, newZone]);

    const fg = featureGroupRef.current;
    if (fg && pendingZoneLayer) {
      fg.removeLayer(pendingZoneLayer);
    }

    setShowNameModal(false);
    setPendingZoneLayer(null);
  };

  const handleNameCancel = () => {
    const fg = featureGroupRef.current;
    if (fg && pendingZoneLayer) {
      fg.removeLayer(pendingZoneLayer);
    }
    setShowNameModal(false);
    setPendingZoneLayer(null);
  };

  const removeZone = (indexToRemove) => {
    setZoneToRemove(indexToRemove);
  };

  const confirmRemoveZone = () => {
    if (zoneToRemove !== null) {
      setZones(prev => prev.filter((_, idx) => idx !== zoneToRemove));
      if (selectedZoneIndex === zoneToRemove) {
        setSelectedZoneIndex(null);
      } else if (selectedZoneIndex > zoneToRemove) {
        setSelectedZoneIndex(prev => prev - 1);
      }
      setZoneToRemove(null);
    }
  };

  const cancelRemoveZone = () => {
    setZoneToRemove(null);
  };

  const toggleZoneActive = (index) => {
    setZones(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], active: !updated[index].active };
      return updated;
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen w-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="w-full flex-1 flex flex-col min-h-0 bg-gray-50 h-[calc(100vh-80px)]">
      <div className="flex justify-between items-center bg-white px-3 md:px-6 py-2 md:py-4 border-b border-gray-200 z-10 shadow-sm relative gap-2">
        <div className="flex items-center gap-1 md:gap-4 flex-1 min-w-0">
          <button
            onClick={() => navigate('/AllRestaurants')}
            className="p-1.5 md:p-2 -ml-1 md:-ml-2 rounded-full hover:bg-gray-100 transition text-gray-600 flex-shrink-0"
            title="Back to All Restaurants"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <h2 className="text-base md:text-xl font-bold text-gray-800 truncate">
            {restaurantName ? `${restaurantName} (${id})` : `Restaurant ${id}`}<span className="hidden md:inline"> - Delivery Zones</span>
          </h2>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button
            onClick={handleSaveZones}
            disabled={saving}
            className={`px-3 py-1.5 md:px-6 md:py-2 text-sm md:text-base bg-blue-600 text-white rounded-md hover:bg-blue-700 transition shadow-sm font-medium ${saving ? 'opacity-70 cursor-not-allowed' : ''}`}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      <div className="flex flex-col md:flex-row flex-1 overflow-hidden relative">
        <div className="flex-1 w-full h-full relative z-0">
          <MapContainer center={mapCenter} zoom={window.innerWidth < 768 ? 12 : ZOOM_LEVEL} style={{ height: '100%', width: '100%' }}>
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />

            <MapUpdater
              selectedZone={selectedZoneIndex !== null ? zones[selectedZoneIndex] : null}
              restaurantLocation={restaurantLocation}
              zones={zones}
              initialFitDone={initialFitDone}
              onFitDone={() => setInitialFitDone(true)}
              focusRestaurant={isRestaurantSelected}
            />
            <MapDeselectOnMove lastFocusRef={lastRestaurantFocusTime} onUserMove={handleUserMapMove} />

            <FeatureGroup ref={featureGroupRef}>
              <EditControl
                position="topright"
                onCreated={onCreated}
                onDrawVertex={() => {
                  // Make the first point black while drawing
                  setTimeout(() => {
                    const markers = document.querySelectorAll('.leaflet-marker-pane .leaflet-div-icon');
                    if (markers.length > 0) {
                      markers[0].style.backgroundColor = 'black';
                      markers[0].style.borderColor = 'white';
                      markers[0].style.borderWidth = '2px';
                      markers[0].style.opacity = '1';
                    }
                  }, 10);
                }}
                draw={{
                  rectangle: false,
                  circle: false,
                  circlemarker: false,
                  marker: false,
                  polyline: false,
                  polygon: {
                    allowIntersection: false,
                    drawError: {
                      color: '#e1e100',
                      message: '<strong>Oh snap!<strong> you can\'t draw that!'
                    },
                    shapeOptions: {
                      color: '#3b82f6'
                    }
                  }
                }}
                edit={{
                  edit: false,
                  remove: false
                }}
              />
            </FeatureGroup>

            {restaurantLocation && (
              <>
                <CircleMarker
                  center={[restaurantLocation.latitude, restaurantLocation.longitude]}
                  radius={18}
                  pathOptions={{ color: '#ef4444', weight: 2, fillColor: '#ef4444', fillOpacity: 0.2 }}
                />
                <Marker position={[restaurantLocation.latitude, restaurantLocation.longitude]}>
                  <Popup>
                    <div className="font-semibold text-gray-800">{restaurantName || `Restaurant ${id}`}</div>
                    <div className="text-xs text-gray-500">Restaurant location</div>
                    <div className="text-xs text-gray-400">{restaurantLocation.latitude.toFixed(6)}, {restaurantLocation.longitude.toFixed(6)}</div>
                  </Popup>
                </Marker>
              </>
            )}

            {zones.map((zone, idx) => {
              const positions = zone.coordinates.map(coord => [coord.latitude, coord.longitude]);
              const isSelected = selectedZoneIndex === idx;
              const color = isSelected ? '#3b82f6' : (zone.active ? '#10b981' : '#6b7280');
              const weight = isSelected ? 4 : 2;
              const fillOpacity = isSelected ? 0.4 : 0.2;

              return (
                <Polygon
                  key={zone._tempId || `zone-${idx}`}
                  positions={positions}
                  pathOptions={{ color, fillColor: color, fillOpacity, weight }}
                >
                  <Popup>
                    <div className="font-semibold text-gray-800">{zone.zoneName}</div>
                    <div className="text-xs text-gray-500">{zone.active ? 'Active' : 'Inactive'}</div>
                  </Popup>
                </Polygon>
              );
            })}
          </MapContainer>

          {/* Mobile Floating Button */}
          <button
            className="md:hidden fixed bottom-6 left-1/2 -translate-x-1/2 bg-blue-600 text-white px-6 py-3 rounded-full shadow-lg font-medium z-[1000] flex items-center gap-2"
            onClick={() => setIsMobileDrawerOpen(true)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
              <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
            </svg>
            View Zones ({zones.length})
          </button>
        </div>

        {/* Mobile Drawer Overlay */}
        {isMobileDrawerOpen && (
          <div
            className="md:hidden fixed inset-0 bg-black bg-opacity-40 z-[1000]"
            onClick={() => setIsMobileDrawerOpen(false)}
          ></div>
        )}

        <div className={`
          fixed bottom-0 left-0 w-full h-[80vh] bg-white rounded-t-3xl shadow-[0_-10px_40px_rgba(0,0,0,0.15)] z-[1001] transition-transform duration-300 flex flex-col overflow-hidden
          md:static md:w-80 md:h-full md:rounded-none md:shadow-lg md:border-l md:border-gray-200 md:translate-y-0 md:z-10
          ${isMobileDrawerOpen ? 'translate-y-0' : 'translate-y-full'}
        `}>
          <div className="p-5 md:p-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center shrink-0">
            <div>
              <h3 className="font-semibold text-gray-800">Configured Zones ({zones.length})</h3>
              <p className="text-xs text-gray-500 mt-1">Use the polygon tool on the map to add new areas.</p>
            </div>
            <button
              className="md:hidden p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-full transition"
              onClick={() => setIsMobileDrawerOpen(false)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="flex-1 p-4 space-y-3 overflow-y-auto">
            {restaurantLocation && (
              <div
                onClick={() => {
                  setIsRestaurantSelected(prev => !prev);
                  setSelectedZoneIndex(null);
                  setIsMobileDrawerOpen(false);
                }}
                title="Click to fly to restaurant location"
                className={`border rounded-lg p-3 shadow-sm hover:shadow-md transition bg-red-50 relative cursor-pointer ${isRestaurantSelected ? 'border-red-500 ring-1 ring-red-500' : 'border-red-200'}`}
              >
                <div className="flex justify-between items-start">
                  <h4 className="font-medium text-gray-800 truncate pr-2">
                    📍 {restaurantName || `Restaurant ${id}`}
                  </h4>
                  <span className="text-[10px] font-semibold text-red-600 bg-red-100 rounded px-2 py-0.5 flex-shrink-0 ml-2">
                    RESTAURANT
                  </span>
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {restaurantLocation.latitude.toFixed(6)}, {restaurantLocation.longitude.toFixed(6)}
                </div>
                <div className="text-[11px] text-red-500 mt-1 font-medium">
                  {isRestaurantSelected ? 'Centered on map ✓' : 'Click to view on map'}
                </div>
              </div>
            )}
            {zones.length === 0 && !restaurantLocation ? (
              <div className="text-center py-8 text-gray-400 text-sm">
                No delivery zones configured yet.
              </div>
            ) : (
              zones.map((zone, idx) => (
                <div
                  key={zone._tempId || `zone-list-${idx}`}
                  onClick={() => {
                    setSelectedZoneIndex(selectedZoneIndex === idx ? null : idx);
                    setIsRestaurantSelected(false);
                    setIsMobileDrawerOpen(false);
                  }}
                  className={`border rounded-lg p-3 shadow-sm hover:shadow-md transition bg-white relative group cursor-pointer ${selectedZoneIndex === idx ? 'border-blue-500 ring-1 ring-blue-500' : 'border-gray-200'}`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-medium text-gray-800 truncate pr-6" title={zone.zoneName}>
                      {zone.zoneName}
                    </h4>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeZone(idx);
                      }}
                      className="text-red-500 hover:text-red-700 transition absolute top-3 right-3"
                      title="Remove Zone"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>

                  <div className="flex items-center justify-between mt-3">
                    <label
                      className="flex items-center cursor-pointer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="relative">
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={zone.active}
                          onChange={() => toggleZoneActive(idx)}
                        />
                        <div className={`block w-8 h-5 rounded-full ${zone.active ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                        <div className={`dot absolute left-1 top-1 bg-white w-3 h-3 rounded-full transition transform ${zone.active ? 'translate-x-3' : ''}`}></div>
                      </div>
                      <div className="ml-2 text-xs font-medium text-gray-600">
                        {zone.active ? 'Active' : 'Inactive'}
                      </div>
                    </label>
                    <span className="text-xs text-gray-500">
                      {zone.coordinates.length} pts
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Modal for naming the zone */}
      {showNameModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-lg p-6 w-96 shadow-xl">
            <h3 className="text-lg font-bold text-gray-800 mb-4">Name Delivery Zone</h3>
            <input
              type="text"
              value={newZoneName}
              onChange={(e) => setNewZoneName(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              placeholder="e.g. Vaishali Nagar"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleNameSubmit();
              }}
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={handleNameCancel}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleNameSubmit}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
              >
                Save Zone
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal for confirming zone removal */}
      {zoneToRemove !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-lg p-6 w-96 shadow-xl">
            <h3 className="text-lg font-bold text-gray-800 mb-2">Remove Delivery Zone</h3>
            <p className="text-sm text-gray-600 mb-6">Are you sure you want to remove the delivery zone <strong>"{zones[zoneToRemove]?.zoneName}"</strong>? This action cannot be undone unless you cancel saving.</p>
            <div className="flex justify-end gap-2">
              <button
                onClick={cancelRemoveZone}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={confirmRemoveZone}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RestaurantDeliveryZones;
